import { Injectable, inject } from "@angular/core";
import { Observable, map } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { SpeciesLoad } from "src/app/backend/models/species/species.load";
import { first, groupBy, split, toPairs } from "lodash";
import { sntTranslate } from "src/app/support/translate";

type SpeciesOption = { key: string; value: string };

@Injectable()
export class SpeciesBackend {
  private readonly http = inject(HttpClient);

  load(request: SpeciesLoad): Observable<SpeciesOption | null> {
    return this.http
      .get<SpeciesOption[]>(`${environment.snoots.api.directory}/species`)
      .pipe(
        map((list: SpeciesOption[]) => list.find((x) => x.key === request.key)),
        map((found) => {
          if (!found) {
            return null;
          }

          return {
            key: found.key,
            value: sntTranslate(found.key)
          };
        })
      );
  }

  listAll(): Observable<Array<{ key: string; value: string }>> {
    return this.http
      .get<Array<{ key: string; value: string }>>(
        `${environment.snoots.api.directory}/species`
      )
      .pipe(
        map((list) => {
          return list.map((x) => ({
            key: x.key,
            value: sntTranslate(x.key)
          }));
        }),
        map((list) => {
          return list.sort((left, right) => {
            return left.value.localeCompare(right.value);
          });
        })
      );
  }

  listAllWithGroup(): Observable<
    Array<{ key: string; items: Array<{ key: string; value: string }> }>
  > {
    return this.listAll().pipe(
      map((results) => {
        return results.map((x) => {
          return {
            ...x,
            groupSpecies: sntTranslate(first(split(x.key, "-")) || "")
          };
        });
      }),
      map((results) =>
        toPairs(groupBy(results, "groupSpecies")).map(([key, value]) => {
          return {
            key,
            items: value
          };
        })
      )
    );
  }

  listAllWithTreeGroup(): Observable<
    Array<{ key: string; value: string; groupCategory?: string }>
  > {
    return this.listAll().pipe(
      map((results) => {
        return results.map((x) => {
          const tokens = split(x.key, "-");
          if (tokens.length > 1) {
            return {
              ...x,
              groupCategory: first(tokens) || ""
            };
          }

          return {
            ...x,
            expanded: true
          };
        });
      })
    );
  }
}
